import React from 'react';
import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { AttributeValue, EventAttributeTypesValues, IAttributesDAO, IEventAttributeRequestDAO } from '../../../../../api/attributes.dao';
import { handleApiException } from '../../../../global/modal/alerts-host/alert-templates';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { AbstractFormViewModelController } from '../../../../../infrastructure/implementations/form-viewmodel-controller';
import { TObject } from '../../../../../infrastructure/type-extensions';
import { FormFieldProps, InitialData } from '../../../../../infrastructure/form-viewmodel-controller';
import { configList, EventAttributeFieldKey } from './event-attribute-form.config';
import { EventAttributeAddValueVM } from './event-attribute-add-value-form/event-attribute-add-value.viewmodel';
import { MAX_EVENT_ATTRIBUTE_VALUES_COUNT } from '../../../../../constants/common.constants';
import { IntlShape } from 'react-intl';
import { ATTRIBUTE_MAX_VALUE_VALIDATION_TEXT, ATTRIBUTE_MIN_VALUE_VALIDATION_TEXT } from './event-attribute-form.text';
import {
    ARE_YOU_SURE_YOU_WANT_TO_TEXT,
    DELETE_EVENT_ATTRIBUTE_TEXT,
    DELETE_THIS_EVENT_ATTRIBUTE_TEXT,
    EVENT_ATTRIBUTE_IN_USE_TEXT,
    OK_TEXT,
} from './event-attribute-form.messages';

export class EventAttributeFormVM {
    private static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'EventAttributeFormVM', configList);
            }
        }();
    }

    @mobx.observable
    public loading: boolean = false;
    @mobx.observable
    private saving = false;
    @mobx.observable
    public values: AttributeValue[] = [];
    @mobx.observable
    private formDataCtrl: AbstractFormViewModelController;
    @mobx.observable
    public isAddValuePopupOpen = false;
    public addValueVM: EventAttributeAddValueVM;
    @mobx.observable
    public addValueError: string = '';
    @mobx.observable
    public valuesError: string = '';
    @mobx.observable
    public valuesData: AttributeValue[] = [];
    @mobx.observable
    public overlayPositionMode: 'edit' | 'add';
    @mobx.observable
    public usedInEventsCount: number = 0;

    constructor(
        private readonly accountId: number,
        private readonly apiCallSuccessCallback: () => void,
        private readonly deleteCallback?: () => void,
        private readonly attributeId?: string,
        @IoC.Inject private readonly attributesDAO: IAttributesDAO = IoC.Container.get(IAttributesDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.formDataCtrl = EventAttributeFormVM.createFormVM();
        this.addValueVM = new EventAttributeAddValueVM(this.handleValueSave.bind(this),{}, this.values, this.closeAddValuePopup);
        if (this.attributeId) {
            this.loadAttributeData();
        }
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    private findValueIndex(value: AttributeValue): number {
        return this.values.findIndex(v => v.label === value.label && v.attributeOrder === value.attributeOrder);
    }

    public getFormFields = (keys: TObject<string>): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(keys)) {
            formFields[key] = this.formDataCtrl.getFormVmValuePropsByKey(key);
        }
        return formFields;
    };

    @mobx.action
    public async createEventAttribute(): Promise<boolean> {
        try {
            const attributesDetails = this.getEventAttributeDTO();
            await this.attributesDAO.createEventAttribute(attributesDetails);

            this.apiCallSuccessCallback();
            return true;
        } catch (error) {
            handleApiException(error);
        }
    }

    @mobx.action
    public async updateEventAttribute(): Promise<boolean> {
        try {
            const attributesDetails = this.getEventAttributeDTO();
            await this.attributesDAO.updateEventAttribute({
                ...attributesDetails,
                eventAttributeId: parseInt(this.attributeId, 10)
            });

            this.apiCallSuccessCallback();
            return true;
        } catch (error) {
            handleApiException(error);
        }
    }

    public deleteEventAttribute = async (): Promise<boolean> => {
        if (this.attributeId) {
            try {
                const details = await this.attributesDAO.getEventAttributeDetails({
                    accountId: this.accountId,
                    eventAttributeId: parseInt(this.attributeId, 10),
                });
                mobx.runInAction(() => {
                    this.usedInEventsCount = details.usedInEventsCount ?? 0;
                });
            } catch (error) {
                handleApiException(error);
                return false;
            }
        }

        if (this.usedInEventsCount > 0) {
            this.alertsService.alert({
                message: this.intl.formatMessage(
                    EVENT_ATTRIBUTE_IN_USE_TEXT,
                    { count: this.usedInEventsCount }
                ),
                okButtonProps: {
                    text: this.intl.formatMessage(OK_TEXT),
                },
            });
            return false;
        }

        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    {this.intl.formatMessage(ARE_YOU_SURE_YOU_WANT_TO_TEXT)}
                    <span className='alert-message--danger'>
                        {' '}{this.intl.formatMessage(DELETE_THIS_EVENT_ATTRIBUTE_TEXT)}
                    </span>?
                </span>
            ),
            confirmButtonProps: {
                text: this.intl.formatMessage(DELETE_EVENT_ATTRIBUTE_TEXT),
                modifier: 'confirm-warn',
                handler: this.deleteEventAttributeApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
        return false;
    };

    private deleteEventAttributeApiCall = async (): Promise<void> => {
        if (!this.attributeId) return;
        try {
            await this.attributesDAO.deleteEventAttribute({
                accountId: this.accountId,
                eventAttributeId: parseInt(this.attributeId, 10),
            });
            if (this.deleteCallback) {
                this.deleteCallback();
            } else {
                this.apiCallSuccessCallback();
            }
        } catch (error) {
            handleApiException(error);
        }
    };

    @mobx.action
    private async loadAttributeData(): Promise<void> {
        if (!this.attributeId) {
            return;
        }

        this.startLoading();
        try {
            const attributeDetails = await this.attributesDAO.getEventAttributeDetails({
                accountId: this.accountId,
                eventAttributeId: parseInt(this.attributeId, 10)
            });

            mobx.runInAction(() => {
                this.formDataCtrl.setValue(EventAttributeFieldKey.NAME, attributeDetails.eventAttributeName);
                this.formDataCtrl.setValue(EventAttributeFieldKey.DESCRIPTION, attributeDetails.description);
                this.formDataCtrl.setValue(EventAttributeFieldKey.EXTERNAL_ID, attributeDetails.externalId);
                this.formDataCtrl.setValue(EventAttributeFieldKey.INPUT_TYPE, attributeDetails.inputType);
                this.formDataCtrl.setValue(EventAttributeFieldKey.IS_ACTIVE, attributeDetails.active);

                this.usedInEventsCount = attributeDetails.usedInEventsCount ?? 0;

                if (attributeDetails.values) {
                    this.values = attributeDetails.values;
                    this.valuesData = attributeDetails.values;
                    this.addValueVM.attributeValues = this.values;
                }
            });
        } catch (error) {
            handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    @mobx.action
    public submitForm = async (intl: IntlShape): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
            return false;
        }

        const inputType = this.formDataCtrl.getValue(EventAttributeFieldKey.INPUT_TYPE) as string;
        if ((inputType === EventAttributeTypesValues['SINGLE_SELECT_DROPDOWN'] || inputType === EventAttributeTypesValues['MULTI_SELECT_DROPDOWN']) && this.values.length === 0) {
            this.valuesError = intl.formatMessage(ATTRIBUTE_MIN_VALUE_VALIDATION_TEXT);
            return false;
        }

        this.valuesError = '';
        this.startSaving();
        const result = this.isEdit()
            ? await this.updateEventAttribute()
            : await this.createEventAttribute();
        this.stopSaving();
        return !!result;
    };

    public isEdit = (): boolean => {
        return !!this.attributeId;
    };

    public get isSaving(): boolean {
        return this.saving;
    }

    public get activeValues() {
        return this.values
            .filter(val => val.active)
            .sort((a, b) => a.attributeOrder - b.attributeOrder);
    }

    public get inactiveValues() {
        return this.values
            .filter(val => !val.active)
            .sort((a, b) => a.attributeOrder - b.attributeOrder);
    }

    @mobx.action
    public addValue(value: AttributeValue) {
        this.addValueError = '';
        this.valuesError = '';
        this.values.push(value);
        this.addValueVM.attributeValues = this.values;
    }

    @mobx.action
    private handleValueSave(value: AttributeValue, originalValueId?: number | undefined) {
        if (originalValueId != null || this.addValueVM.editingValue) {
            this.updateValue(value, originalValueId);
        } else {
            this.addValue(value);
        }
    }

    @mobx.action
    public updateValue(value: AttributeValue, originalValueId: number | undefined) {
        this.addValueError = '';
        this.valuesError = '';

        if (originalValueId !== undefined) {
            const index = this.values.findIndex(v => v.valueId === originalValueId);
            if (index !== -1) {
                this.values[index] = {
                    ...value,
                    valueId: originalValueId,
                    attributeOrder: this.values[index].attributeOrder
                };
            }
        } else {
            const originalValue = this.addValueVM.editingValue;
            if (originalValue) {
                const index = this.findValueIndex(originalValue);
                if (index !== -1) {
                    this.values[index] = {
                        ...value,
                        attributeOrder: this.values[index].attributeOrder
                    };
                }
            }
        }
        this.addValueVM.attributeValues = this.values;
    }

    private getEventAttributeDTO = (): IEventAttributeRequestDAO => {
        let attributeValues: AttributeValue[] = [];
        if (this.formDataCtrl.getValue(EventAttributeFieldKey.INPUT_TYPE) === EventAttributeTypesValues['SINGLE_SELECT_DROPDOWN'] || this.formDataCtrl.getValue(EventAttributeFieldKey.INPUT_TYPE) === EventAttributeTypesValues['MULTI_SELECT_DROPDOWN']) {
            attributeValues = this.values;
        }
        return {
            accountId: this.accountId,
            eventAttributeName: (this.formDataCtrl.getValue(EventAttributeFieldKey.NAME) as string).trim(),
            active: (this.formDataCtrl.getValue(EventAttributeFieldKey.IS_ACTIVE) as boolean),
            inputType: (this.formDataCtrl.getValue(EventAttributeFieldKey.INPUT_TYPE) as string).trim(),
            description: (this.formDataCtrl.getValue(EventAttributeFieldKey.DESCRIPTION) as string).trim(),
            externalId: (this.formDataCtrl.getValue(EventAttributeFieldKey.EXTERNAL_ID) as string).trim(),
            values: attributeValues,
        } as IEventAttributeRequestDAO;
    };

    @mobx.action
    private startLoading(): void {
        this.loading = true;
    }

    @mobx.action
    private stopLoading(): void {
        this.loading = false;
    }

    @mobx.action
    private startSaving = (): void => {
        this.saving = true;
    };

    @mobx.action
    private stopSaving = (): void => {
        this.saving = false;
    };

    @mobx.action
    public openAddValuePopup = (intl: IntlShape, value?: AttributeValue): void => {
        if (!value && this.values.length >= MAX_EVENT_ATTRIBUTE_VALUES_COUNT) {
            this.addValueError = intl.formatMessage(ATTRIBUTE_MAX_VALUE_VALIDATION_TEXT);
            return;
        }
        this.overlayPositionMode = value ? 'edit' : 'add';
        this.addValueVM.attributeValues = this.values;
        this.isAddValuePopupOpen = true;
        this.addValueError = '';
        this.addValueVM.open(value);
    };

    @mobx.action
    public closeAddValuePopup = (): void => {
        this.isAddValuePopupOpen = false;
        this.overlayPositionMode = 'add';
    };

    @mobx.action
    public clearValuesError = (): void => {
        this.valuesError = '';
    };

    @mobx.action
    public move(sourceIndex: number, destinationIndex: number): void {
        const activeValues = this.activeValues.slice();
        const field = activeValues[sourceIndex];

        if (sourceIndex < destinationIndex) {
            activeValues.splice(destinationIndex, 0, field);
            activeValues.splice(sourceIndex, 1);
        } else {
            activeValues.splice(sourceIndex, 1);
            activeValues.splice(destinationIndex, 0, field);
        }

        this.updateActiveValuesOrder(activeValues);
    }

    @mobx.action
    public reorder(sourceIndex: number, destinationIndex: number): void {
        const activeValues = this.activeValues.slice();
        const field = activeValues[sourceIndex];

        activeValues.splice(sourceIndex, 1);
        activeValues.splice(destinationIndex, 0, field);
        this.updateActiveValuesOrder(activeValues);
    }

    @mobx.action
    private updateActiveValuesOrder(activeValues: AttributeValue[]): void {
        const inactiveValues = this.inactiveValues;
        const updatedActiveValues = activeValues.map((field, i) => ({ ...field, attributeOrder: i }));
        const updatedInactiveValues = inactiveValues.map((field, i) => ({
            ...field,
            attributeOrder: updatedActiveValues.length + i
        }));

        this.values = [...updatedActiveValues, ...updatedInactiveValues];
    }
}
